import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Paper, Button,
  alpha, useTheme,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  NotificationsActive as AlertsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

interface StatCard {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  action?: string;
  onAction?: () => void;
}

interface RecentSend {
  history_id: string;
  schedule_name: string;
  alert_sent_at: string;
  assets_total: number;
  assets_completed: number;
  assets_pending: number;
  email_sent: number;
  roundup_id: string | null;
}

const OverviewTab: React.FC<{ onNavigate?: (tab: string) => void }> = ({ onNavigate }) => {
  const theme = useTheme();
  const token = localStorage.getItem('token');

  const [scheduleCount, setScheduleCount] = useState<number | null>(null);
  const [roundupCount, setRoundupCount] = useState<number | null>(null);
  const [alertRuleCount, setAlertRuleCount] = useState<number | null>(null);
  const [recentSends, setRecentSends] = useState<RecentSend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };

    const fetchAll = async () => {
      try {
        const [schedulesRes, roundupsRes, alertsRes, historyRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/template-check-schedules/all`, { headers }).catch(() =>
            axios.get(`${API_BASE_URL}/template-check-schedules`, { headers })
          ),
          axios.get(`${API_BASE_URL}/communication-roundups`, { headers }),
          axios.get(`${API_BASE_URL}/communication-alerts`, { headers }),
          axios.get(`${API_BASE_URL}/communication-roundups/history/recent`, { headers }).catch(() => null),
        ]);

        if (schedulesRes.status === 'fulfilled') {
          const d = schedulesRes.value?.data;
          const arr = Array.isArray(d) ? d : d?.data ?? [];
          setScheduleCount(arr.filter((s: any) => s.enabled).length);
        }
        if (roundupsRes.status === 'fulfilled') {
          const d = roundupsRes.value?.data?.data ?? [];
          setRoundupCount(d.filter((r: any) => r.enabled).length);
        }
        if (alertsRes.status === 'fulfilled') {
          const d = alertsRes.value?.data?.data ?? [];
          setAlertRuleCount(d.filter((r: any) => r.enabled).length);
        }
        if (historyRes.status === 'fulfilled' && historyRes.value) {
          setRecentSends(historyRes.value?.data?.data ?? []);
        }
      } catch (_) {
        // Fail silently
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [token]);

  const statCards: StatCard[] = [
    {
      label: 'Active Schedules',
      value: scheduleCount ?? '—',
      icon: <ScheduleIcon />,
      color: theme.palette.primary.main,
      action: 'View',
      onAction: () => onNavigate?.('schedules'),
    },
    {
      label: 'Active Round-Ups',
      value: roundupCount ?? '—',
      icon: <RepeatIcon />,
      color: theme.palette.secondary.main,
      action: 'View',
      onAction: () => onNavigate?.('roundups'),
    },
    {
      label: 'Enabled Alert Rules',
      value: alertRuleCount ?? '—',
      icon: <AlertsIcon />,
      color: theme.palette.warning.main,
      action: 'View',
      onAction: () => onNavigate?.('alerts'),
    },
  ];

  return (
    <Box>
      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: alpha(card.color, 0.12), color: card.color, mr: 2,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {loading ? <CircularProgress size={20} /> : card.value}
                    </Typography>
                  </Box>
                </Box>
                {card.action && (
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={card.onAction}
                    sx={{ textTransform: 'none', p: 0 }}
                  >
                    {card.action}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent send log */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Recent Sends</Typography>
      {recentSends.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No recent sends found</Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Schedule</TableCell>
                <TableCell>Sent at</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Completed</TableCell>
                <TableCell align="center">Pending</TableCell>
                <TableCell align="center">Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentSends.slice(0, 20).map((row) => (
                <TableRow key={row.history_id} hover>
                  <TableCell>{row.schedule_name}</TableCell>
                  <TableCell>{new Date(row.alert_sent_at).toLocaleString('en-GB')}</TableCell>
                  <TableCell align="center">{row.assets_total}</TableCell>
                  <TableCell align="center">{row.assets_completed}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.assets_pending}
                      size="small"
                      color={row.assets_pending > 0 ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.roundup_id ? 'Round-Up' : 'Alert'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default OverviewTab;
